import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deleteNonConvertedAccount,
  getAllCompanies,
  getAllFreelancers,
  getPortageCompanies,
  getSuperAdminStaffedFree,
  pushSuperAdminStaffedFreeUsage,
  updateStaffedFreeSubscriptionAmount,
} from '@/apis/superAdmin'
import { useApiReady } from '@/hooks/useApiReady'

export type SuperAdminSection = 'companies' | 'freelancers' | 'portage'

// Query keys
export const superAdminDataKeys = {
  all: ['superAdminData'] as const,
  lists: () => [...superAdminDataKeys.all, 'list'] as const,
  list: (activeTab: string, filters: unknown) => [...superAdminDataKeys.lists(), activeTab, filters] as const,
  staffedFree: (month: string, filters: unknown) => [...superAdminDataKeys.all, 'staffedFree', month, filters] as const,
}

export interface SuperAdminDataFilters {
  page?: number
  limit?: number
  search?: string
  is_test?: boolean
  team_id?: string
}

export interface SuperAdminListResult {
  items: unknown[]
  total: number
}

async function fetchSuperAdminList(
  activeTab: SuperAdminSection,
  filters: SuperAdminDataFilters
): Promise<SuperAdminListResult> {
  const params: Record<string, string | number | boolean | undefined> = {
    page: filters.page,
    limit: filters.limit,
  }

  if (filters.search) {
    params.search = filters.search
  }

  if (filters.is_test !== undefined) {
    params.is_test = filters.is_test
  }

  if (filters.team_id) {
    params.team_id = filters.team_id
  }

  const res =
    activeTab === 'companies'
      ? await getAllCompanies(params)
      : activeTab === 'portage'
        ? await getPortageCompanies(params)
        : await getAllFreelancers(params)

  if (res?.code === 200) {
    return {
      items: res.data?.items || [],
      total: res.data?.total || 0,
    }
  }

  return { items: [], total: 0 }
}

export function getSuperAdminListQueryOptions(
  activeTab: SuperAdminSection,
  filters: SuperAdminDataFilters
) {
  return {
    queryKey: superAdminDataKeys.list(activeTab, filters),
    queryFn: () => fetchSuperAdminList(activeTab, filters),
  }
}

/** Liste companies/freelancers — défaut page login super-admin (onglet companies). */
export const SUPER_ADMIN_DEFAULT_LIST_FILTERS: SuperAdminDataFilters = {
  page: 1,
  limit: 50,
}

/**
 * Hook pour récupérer les données super admin (companies ou freelancers)
 */
export function useSuperAdminData(
  activeTab: SuperAdminSection,
  filters: SuperAdminDataFilters,
  options?: { enabled?: boolean }
) {
  const apiReady = useApiReady()

  return useQuery({
    ...getSuperAdminListQueryOptions(activeTab, filters),
    enabled: apiReady && options?.enabled !== false,
  })
}

export interface SuperAdminCompanyOption {
  value: string
  label: string
}

/** Options équipe pour le filtre freelances super-admin (aligné sur l’onglet test/prod). */
export function useSuperAdminCompanyOptions(
  isTest: boolean,
  options?: { enabled?: boolean },
) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: [...superAdminDataKeys.all, 'companyOptions', isTest] as const,
    queryFn: async (): Promise<SuperAdminCompanyOption[]> => {
      const res = await getAllCompanies({ page: 1, limit: 500, is_test: isTest })
      const items = (res?.data?.items || []) as Array<{
        id?: string
        name?: string
        is_waiting_admin_row?: boolean
      }>

      return items
        .filter((company) => company.id && !company.is_waiting_admin_row)
        .map((company) => ({
          value: company.id as string,
          label: company.name?.trim() || (company.id as string),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    },
    enabled: apiReady && options?.enabled !== false,
    staleTime: 1000 * 60 * 5,
  })
}


export interface StaffedFreelancerMission {
  id: string
  name: string | null
  status: string | null
  start_date: string | null
  end_date: string | null
}

export interface StaffedFreelancer {
  freelancer_id: string
  freelancer_name: string
  missions: StaffedFreelancerMission[]
}

export interface StaffedFreeRow {
  team_id: string
  team_name: string
  staffed_count: number
  pending_signature_count: number
  pending_signature_revenue: number
  counts_toward_lost: boolean
  subscription_status: string | null
  subscription_canceled_at: string | null
  subscription_amount: number
  billing_free_trial_enabled: boolean
  is_legacy: boolean
  is_manual_billing: boolean
  usage_status: string | null
  usage_quantity: number | null
  usage_error_message: string | null
  freelancers: StaffedFreelancer[]
  /** Équipe réintroduite manuellement (client payant hors app). */
  forced?: boolean
  /** Mode ESN équipe → paliers metered Stripe 17/15/11 €. */
  is_esn?: boolean
}

export interface StaffedFreeMetrics {
  newCompanies: number
  newCompaniesPercent: number | null
  activeCompanies: number
  activeCompaniesPercent: number | null
  totalStaffed: number
  totalStaffedPercent: number | null
  pendingSignatureTotal: number
  missedRevenue: number
  missedRevenuePercent: number | null
  mrr: number
  mrrPercent: number | null
}

export interface SuperAdminStaffedFreeData {
  items: StaffedFreeRow[]
  total: number
  month: string
  metrics: StaffedFreeMetrics
}

export interface SuperAdminStaffedFreeFilters {
  search?: string
}

/**
 * Hook pour récupérer les équipes staffées gratuitement (sans billing réussi) pour un mois donné
 */
export function useSuperAdminStaffedFree(
  month: string,
  filters: SuperAdminStaffedFreeFilters = {},
  options?: { enabled?: boolean }
) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: superAdminDataKeys.staffedFree(month, filters),
    queryFn: async (): Promise<SuperAdminStaffedFreeData | null> => {
      const params: Record<string, string> = {}
      if (filters.search) params.search = filters.search
      const res = await getSuperAdminStaffedFree(month, params)
      if (res?.code === 200 && res?.data) {
        return res.data as SuperAdminStaffedFreeData
      }
      return null
    },
    enabled: apiReady && options?.enabled !== false && !!month,
  })
}

/**
 * Mutation pour mettre à jour le montant de l'abonnement mensuel HT d'une équipe.
 */
export function useUpdateSubscriptionAmount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, amount }: { teamId: string; amount: number }) =>
      updateStaffedFreeSubscriptionAmount(teamId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminDataKeys.all })
    },
  })
}

/**
 * Mutation pour pousser manuellement un usage record Stripe pour une équipe et un mois donnés.
 * Inclut les abonnements en trialing (contrairement au cron).
 */
export function usePushStaffedFreeUsage(month: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (teamId: string) => pushSuperAdminStaffedFreeUsage(teamId, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminDataKeys.all })
    },
  })
}

export type DeleteNonConvertedCompanyPayload = {
  teamId?: string
  profileId?: string
  reason: 'cgu_non_converted' | 'abuse_spam' | 'other'
  reason_detail?: string
}

export function useDeleteNonConvertedCompany() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, profileId, reason, reason_detail }: DeleteNonConvertedCompanyPayload) =>
      deleteNonConvertedAccount({ teamId, profileId, reason, reason_detail }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminDataKeys.all })
    },
  })
}
